// ─── useAuditLog.js ───────────────────────────────────────────────────────────
// Hook that returns a log() function to record any action to the audit_logs table.
// Import as: import { useAuditLog } from "./useAuditLog";
//
// Usage:
//   const { log } = useAuditLog(company, currentUser);
//   await log({ action: "tm_rule_updated", module: "transaction_monitoring",
//               entityId: rule.id, entityName: rule.name,
//               oldValue: { threshold: 9000 }, newValue: { threshold: 12000 } });

export function useAuditLog(company, currentUser) {
  const insertLog = async (logData, retryCount = 0) => {
    let token = localStorage.getItem('amlguard_token');
    if (!token) {
      console.warn("[audit] No auth token available");
      return;
    }

    try {
      const response = await fetch(`/api/db/audit-logs?company-id=${logData.company_id}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
          'x-company-id': logData.company_id,
        },
        body: JSON.stringify(logData),
      });
      if (!response.ok) {
        const errorText = await response.text();
        try {
          const error = JSON.parse(errorText);
          // If auth failed and we haven't retried, try to refresh token
          if (response.status === 401 && retryCount === 0) {
            console.warn("[audit] Auth token invalid, attempting refresh...");
            const refreshRes = await fetch('/api/auth/refresh', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
            });
            if (refreshRes.ok) {
              const refreshData = await refreshRes.json();
              if (refreshData.token) {
                localStorage.setItem('amlguard_token', refreshData.token);
                // Retry with new token
                return insertLog(logData, retryCount + 1);
              }
            }
          }
          console.warn("[audit] insert failed:", error.error);
        } catch {
          console.warn("[audit] insert failed:", response.status, errorText);
        }
      }
    } catch (err) {
      console.warn("[audit] insert error:", err.message);
    }
  };

  async function log({
    action,
    module,
    entityId   = null,
    entityName = null,
    oldValue   = null,
    newValue   = null,
    meta       = null,
  }) {
    if (!company?.id) {
      console.warn("[audit] No company_id available");
      return;
    }
    if (!currentUser) {
      console.warn("[audit] No currentUser available");
      return;
    }
    console.log('[audit] Logging action:', action, 'for company:', company.id);
    await insertLog({
      company_id:  company.id,
      userId:      currentUser.id,
      userEmail:   currentUser.email,
      userName:    `${currentUser.firstName || ""} ${currentUser.lastName || ""}`.trim(),
      action,
      module,
      entityId:    entityId,
      entityName:  entityName,
      oldValue:    oldValue,
      newValue:    newValue,
      meta,
    });
  }

  async function logFailure({ action, module, entityId = null, entityName = null, errorMessage }) {
    if (!company?.id || !currentUser) return;
    await insertLog({
      company_id:  company.id,
      userId:      currentUser.id,
      userEmail:   currentUser.email,
      userName:    `${currentUser.firstName || ""} ${currentUser.lastName || ""}`.trim(),
      action:      `${action}_failed`,
      module,
      entityId:    entityId,
      entityName:  entityName,
      oldValue:    null,
      newValue:    { error: errorMessage },
      meta:        { failed: true },
    });
  }

  return { log, logFailure };
}